import { claimflowApiClient } from './claimflowApiClient';
import { toWorkflowExecutionError } from './taskService';
import type { WorkflowExecutionData } from './taskService';

/**
 * Claim closure (« Clôture ») API service — mahhp 22846.
 *
 * - `boby-ws2-gs-sel-motif-cloture` → `GET /boBy/v2/WS2_GS_SEL_MOTIF_CLOTURE`
 *   (query `perPage` only — the list does not depend on the claim): the
 *   closure motifs for the « Motif » select, in the standard list envelope
 *   `{ total, perPage, page, totalPage, from, to, data: [{ CD_MOT, LIB_MOT }] }`
 *   wrapped by origami's ERP proxy (`ErpProxyResponse<T>`). VEOS pads some
 *   labels with trailing spaces (`"Annulation dossier courtier "`), so labels
 *   are trimmed for display.
 *
 * The closure is ONE call to the `claimflow-close-claim` backend workflow
 * (`GET /sinistre/{id_sin}?inline=1` → `PUT /sinistre/{id_sin}` replaying the
 * full flux with `mouvement = "CLOSIN"` and `motif` replaced, server-side). A
 * failed run (HTTP 422 — e.g. an empty motif rejected by the workflow) surfaces
 * as a `WorkflowExecutionError` carrying the step trail, exactly like
 * `taskService.closeTask`.
 */

interface ErpProxyResponse<T> {
  success: boolean;
  http_status?: number;
  data: T;
  error?: { code: string; message: string } | null;
}

/** The standard VEOS list envelope (`data` may be omitted on an empty result). */
interface VeosListEnvelope<T> {
  total?: number;
  perPage?: number;
  page?: number;
  totalPage?: number;
  from?: number | null;
  to?: number | null;
  data?: T[] | null;
}

/** One row of `WS2_GS_SEL_MOTIF_CLOTURE` — a claim closure motif. */
export interface VeosMotifClotureRow {
  /** Motif code (e.g. `"ANULCRT"`, `"112"`) — the `motif` sent to the workflow. */
  CD_MOT: string;
  /** Display label — may carry trailing spaces on VEOS' side. */
  LIB_MOT: string | null;
}

/** A « Motif » select option (`code` = `CD_MOT`, `label` = trimmed `LIB_MOT`). */
export interface MotifClotureOption {
  code: string;
  label: string;
}

const SEL_MOTIF_CLOTURE_URL =
  '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-motif-cloture';

const CLOSE_CLAIM_WORKFLOW_URL = '/claimflow/workflows/claimflow-close-claim/execute';

/** Client-side guards of {@link clotureService.closeClaim} (never reach the API). */
const CLOSE_CLAIM_GUARD_TEXT = {
  invalidClaim: 'Identifiant de sinistre invalide.',
  missingMotif: 'Sélectionnez un motif de clôture.',
} as const;

function unwrap<T>(envelope: ErpProxyResponse<T>): T {
  if (!envelope.success) {
    throw new Error(envelope.error?.message ?? 'ERP request failed');
  }
  return envelope.data;
}

/** The claim id as the number the workflow's `id_sin` input expects. */
function toClaimId(idSin: number | string): number | null {
  if (typeof idSin === 'number') return Number.isFinite(idSin) ? idSin : null;
  const trimmed = idSin.trim();
  if (trimmed === '') return null;
  const id = Number(trimmed);
  return Number.isFinite(id) ? id : null;
}

export const clotureService = {
  /**
   * The closure motifs for the « Motif » select (`WS2_GS_SEL_MOTIF_CLOTURE`),
   * in VEOS order. `perPage: 9999` overrides VEOS's default 15-row page (18
   * rows today) — the codebase convention for full lists. Labels are trimmed;
   * an empty label falls back to the code.
   *
   * @throws when the proxy reports a failure.
   */
  async fetchMotifsCloture(): Promise<MotifClotureOption[]> {
    const response = await claimflowApiClient.get<
      ErpProxyResponse<VeosListEnvelope<VeosMotifClotureRow> | null>
    >(SEL_MOTIF_CLOTURE_URL, { params: { perPage: 9999 } });

    const rows = unwrap(response.data)?.data ?? [];
    return rows.map((row) => {
      const code = row.CD_MOT;
      const label = (row.LIB_MOT ?? '').trim();
      return { code, label: label === '' ? code : label };
    });
  },

  /**
   * Close the claim through the `claimflow-close-claim` workflow — one call.
   * `context_data` carries `id_sin` (as a number) and the chosen `motif`
   * (`CD_MOT`). An empty motif or a non-numeric claim id is rejected before
   * any request: the workflow would refuse an empty motif with a 422 anyway.
   *
   * @throws {WorkflowExecutionError} when the workflow run fails (HTTP 422) —
   *   carries the backend message and the step trail.
   */
  async closeClaim(idSin: number | string, motif: string): Promise<WorkflowExecutionData> {
    const claimId = toClaimId(idSin);
    if (claimId === null) {
      throw new Error(CLOSE_CLAIM_GUARD_TEXT.invalidClaim);
    }
    if (motif.trim() === '') {
      throw new Error(CLOSE_CLAIM_GUARD_TEXT.missingMotif);
    }

    try {
      const response = await claimflowApiClient.post<{ data: WorkflowExecutionData }>(
        CLOSE_CLAIM_WORKFLOW_URL,
        { context_data: { id_sin: claimId, motif } },
      );
      return response.data.data;
    } catch (error) {
      const workflowError = toWorkflowExecutionError(error);
      if (workflowError) throw workflowError;
      throw error;
    }
  },
};
